import React from "react";
import {
  Button,
  Col,
  DatePicker,
  Drawer,
  Form,
  Input,
  Row,
  Select,
  Spin,
  theme,
  Typography,
  Divider,
  Avatar,
  Space,
} from "antd";
import {
  LoadingOutlined,
  UserOutlined,
  CloseOutlined,
  UserAddOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { useForm } from "antd/es/form/Form";
import { adminContext } from "../../Common/PageRoute";
import { Utils } from "../../Utilities/Utils";
import dayjs from "dayjs";
import UserService, {
  roleOption,
  supportRoleOption,
} from "../../Services/UserService";
import { useUser } from "../../Context/UserContext";

const { Text } = Typography;

const AddUser = ({ onDismiss, practiceId, userId }: any) => {
  const { token } = theme.useToken();
  const { isSuperAdmin, isSubAdmin, isSupportAdmin } = useUser();
  const isSupportAdminOnly =
    isSupportAdmin() && !isSuperAdmin() && !isSubAdmin();
  const [form] = useForm();
  const { openNotification } = React.useContext(adminContext);
  const [loading, setLoading] = React.useState(false);

  const val = {
    address: {
      country: "United Kingdom",
    },
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      setLoading(true);

      let userValues = {
        ...form.getFieldsValue(),
        birthDate: form.getFieldValue("birthDate")
          ? dayjs(form.getFieldValue("birthDate"), "DD/MM/YYYY").format(
              "YYYY-MM-DD",
            )
          : undefined,
      };

      const res = await UserService.createNewUser(userValues);
      if (res.status) {
        onDismiss(true);
        openNotification(
          "success",
          `New user ${res?.result?.name?.first + " " + res?.result?.name?.last} successfully created.`,
        );
      } else {
        openNotification("error", res.message);
      }
    } catch (error) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      title={null}
      placement="right"
      width={560}
      onClose={() => onDismiss(false)}
      open={true}
      destroyOnClose
      closeIcon={null}
      styles={{
        header: { display: "none" },
        body: {
          padding: 0,
          overflowX: "hidden",
          background: token.colorBgContainer,
        },
        footer: {
          borderTop: `1px solid ${token.colorBorderSecondary}`,
          padding: "14px 24px",
          textAlign: "right",
          background: token.colorBgContainer,
        },
      }}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <Button onClick={() => onDismiss(false)} disabled={loading}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleSubmit} loading={loading}>
            Save User
          </Button>
        </div>
      }
    >
      <Spin
        spinning={loading}
        indicator={<LoadingOutlined style={{ fontSize: 28 }} spin />}
      >
        {/* Synchronized Header Banner */}
        <div
          style={{
            padding: "16px 48px 16px 20px",
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            position: "relative",
          }}
        >
          <Button
            type="text"
            icon={
              <CloseOutlined style={{ color: token.colorIcon, fontSize: 16 }} />
            }
            onClick={(e) => {
              e.stopPropagation();
              onDismiss(false);
            }}
            style={{
              position: "absolute",
              top: 14,
              right: 16,
              width: 32,
              height: 32,
              borderRadius: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              cursor: "pointer",
            }}
          />

          <Row align="middle" gutter={16}>
            <Col>
              <Avatar
                size={52}
                style={{
                  backgroundColor: token.colorPrimary,
                  fontSize: 22,
                  color: token.colorTextLightSolid,
                  border: `2px solid ${token.colorBorderSecondary}`,
                  boxShadow: token.boxShadowSecondary,
                }}
                icon={<UserAddOutlined />}
              />
            </Col>
            <Col flex="1">
              <Space direction="vertical" size={2}>
                <Text
                  style={{
                    color: token.colorTextHeading,
                    fontSize: 17,
                    fontWeight: 700,
                    letterSpacing: "-0.01em",
                  }}
                >
                  Add New User
                </Text>
                <Text
                  style={{ color: token.colorTextSecondary, fontSize: 12.5 }}
                >
                  Create a new admin or support user account
                </Text>
              </Space>
            </Col>
          </Row>
        </div>

        {/* Synchronized Form Container */}
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={false}
          initialValues={val}
          style={{ padding: "20px 24px 24px" }}
        >
          {/* Section 1: Profile Information */}
          <div style={{ marginBottom: 20 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 14,
              }}
            >
              <UserOutlined
                style={{ color: token.colorPrimary, fontSize: 14 }}
              />
              <Text
                strong
                style={{
                  fontSize: 12,
                  color: token.colorTextHeading,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Profile Information
              </Text>
            </div>

            <Row gutter={[12, 14]}>
              <Col span={6}>
                <Form.Item
                  label="Title"
                  name={["name", "title"]}
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    placeholder="Title"
                    options={[
                      { value: "Mr", label: "Mr" },
                      { value: "Mrs", label: "Mrs" },
                      { value: "Miss", label: "Miss" },
                      { value: "Sir", label: "Sir" },
                      { value: "Ms", label: "Ms" },
                      { value: "Dr", label: "Dr" },
                      { value: "Prof", label: "Prof" },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={9}>
                <Form.Item
                  label="First name"
                  name={["name", "first"]}
                  style={{ marginBottom: 0 }}
                  rules={[
                    { required: true, message: "First name is required" },
                    { validator: Utils.validateWhiteSpace },
                  ]}
                >
                  <Input
                    placeholder="First name"
                    onKeyPress={(e: any) => Utils.ValidateCharOnly(e)}
                    onInput={(e: any) =>
                      (e.target.value =
                        e.target.value.length > 1
                          ? e.target.value
                          : e.target.value.toUpperCase())
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={9}>
                <Form.Item
                  label="Last name"
                  name={["name", "last"]}
                  style={{ marginBottom: 0 }}
                  rules={[
                    { required: true, message: "Last name is required" },
                    { validator: Utils.validateWhiteSpace },
                  ]}
                >
                  <Input
                    placeholder="Last name"
                    onKeyPress={(e: any) => Utils.ValidateCharOnly(e)}
                    onInput={(e: any) =>
                      (e.target.value =
                        e.target.value.length > 1
                          ? e.target.value
                          : e.target.value.toUpperCase())
                    }
                  />
                </Form.Item>
              </Col>

              <Col span={14}>
                <Form.Item
                  label="Email address"
                  name="email"
                  style={{ marginBottom: 0 }}
                  rules={[
                    { required: true, message: "Email is required" },
                    { validator: Utils.ValidateEmailFormat },
                  ]}
                >
                  <Input placeholder="Email address" />
                </Form.Item>
              </Col>
              <Col span={10}>
                <Form.Item
                  label="Phone number"
                  name="phoneNumber"
                  style={{ marginBottom: 0 }}
                >
                  <Input
                    placeholder="Phone number"
                    onKeyPress={(e) => Utils.ValidateNumberOnly(e)}
                    maxLength={10}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Gender"
                  name="gender"
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    placeholder="Select gender"
                    options={[
                      { value: 1, label: "Male" },
                      { value: 2, label: "Female" },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Birth date"
                  name="birthDate"
                  style={{ marginBottom: 0 }}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    placeholder="DD/MM/YYYY"
                    format="DD/MM/YYYY"
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <Divider
            style={{
              margin: "16px 0 20px",
              borderColor: token.colorBorderSecondary,
            }}
          />

          {/* Section 2: Role & Authorization */}
          <div style={{ marginBottom: 20 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 14,
              }}
            >
              <SafetyCertificateOutlined
                style={{ color: token.colorPrimary, fontSize: 14 }}
              />
              <Text
                strong
                style={{
                  fontSize: 12,
                  color: token.colorTextHeading,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Role & Authorization
              </Text>
            </div>

            <Row gutter={[12, 14]}>
              <Col span={24}>
                <Form.Item
                  label="Assigned System Role"
                  name="role"
                  style={{ marginBottom: 0 }}
                  rules={[
                    { required: true, message: "Please select a system role" },
                  ]}
                  help={
                    <span
                      style={{
                        fontSize: 11.5,
                        color: token.colorTextSecondary,
                        marginTop: 4,
                        display: "block",
                      }}
                    >
                      Select the system role to assign permission scopes for the
                      new user.
                    </span>
                  }
                >
                  <Select
                    placeholder="Select role"
                    options={isSuperAdmin() ? roleOption : supportRoleOption}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>
        </Form>
      </Spin>
    </Drawer>
  );
};

export default AddUser;
